import dayjs from 'dayjs';
import { IconButton } from '@mui/material';
import {
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { Badge } from '@/components';
import { RouteStatus } from '@/pages/Business/pages/Monitoring/enums';
import { formatRouteScheduledCalendarDay, formatTime } from '@/pages/Business/pages/Monitoring/utils';
import { getRouteStatusLabel } from '@/pages/Business/pages/Monitoring/utils/statusMapper';
import { STATUS_COLORS } from '@/pages/Business/pages/Monitoring/constants/routeCard.constants';
import type { RouteTableRowData } from '@/pages/Business/pages/Monitoring/types/routeTable';

interface RouteExpandToggleProps {
  expanded: boolean;
  onToggle: () => void;
}

export const RouteExpandToggle = ({
  expanded,
  onToggle,
}: RouteExpandToggleProps) => (
  <IconButton
    size='small'
    onClick={onToggle}
    aria-expanded={expanded}
    aria-label={expanded ? 'Contraer detalle' : 'Expandir detalle'}
  >
    {expanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
  </IconButton>
);

const cellTextClass = 'text-lg leading-snug text-neutral-900';

const VISIT_METRIC_TONE: Record<'completed' | 'failed' | 'pending' | 'expired', string> = {
  completed: 'text-emerald-600',
  failed: 'text-red-500',
  pending: 'text-gray-500',
  expired: 'text-gray-500',
};

export const RouteIdCell = ({ id }: { id: string }) => {
  const trimmed = id.trim();
  if (trimmed === '') {
    return <span className='text-neutral-400 tabular-nums'>—</span>;
  }
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(trimmed);
      }}
      className='flex items-center gap-1.5 rounded-md px-4 py-1.5 text-xs font-semibold text-white bg-[var(--primary-color)] transition-colors hover:bg-orange-400 active:bg-orange-500 shadow-sm whitespace-nowrap cursor-pointer'
      title='Copiar ID de ruta'
    >
      <CopyIcon sx={{ fontSize: '0.875rem' }} />
      <span>Copiar ID</span>
    </button>
  );
};


export const RouteVisitStatusMetricCell = ({
  value,
  variant,
}: {
  value: number | null;
  variant: 'completed' | 'failed' | 'pending' | 'expired';
}) => {
  if (value === null) {
    return (
      <span
        className='text-lg text-neutral-400'
        title='Dato no disponible en el listado'
      >
        —
      </span>
    );
  }
  return (
    <span
      className={`tabular-nums text-lg font-semibold leading-snug antialiased ${VISIT_METRIC_TONE[variant]}`}
    >
      {value}
    </span>
  );
};

export const RouteNameCell = ({
  name,
  onClick,
}: {
  name: string;
  onClick?: () => void;
}) => (
  <span
    className={`text-lg leading-snug font-black antialiased ${
      onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
    }`}
    style={{ color: 'var(--primary-color)' }}
    onClick={(e) => {
      if (onClick) {
        e.stopPropagation();
        onClick();
      }
    }}
  >
    {name || 'Ruta desde App'}
  </span>
);


export const RouteOrdersCountCell = ({ count }: { count: number }) => (
  <span className={`tabular-nums ${cellTextClass}`}>{count}</span>
);

export const RouteDateCell = ({
  scheduled,
  scheduledCalendarDate,
}: {
  scheduled: string;
  scheduledCalendarDate?: string | null;
}) => (
  <span className={`tabular-nums ${cellTextClass}`}>
    {formatRouteScheduledCalendarDay({ scheduled, scheduledCalendarDate })}
  </span>
);

export const RouteTimeCell = ({ value }: { value: string | null }) => (
  <span className={`tabular-nums ${cellTextClass}`}>
    {value ? formatTime(value) : '—'}
  </span>
);

export const RouteFinCell = ({ value, scheduled }: { value: string | null; scheduled: string }) => {
  if (!value) return <span className='tabular-nums text-lg leading-snug text-neutral-600'>—</span>;
  const isNextDay = dayjs(value).format('YYYY-MM-DD') !== dayjs(scheduled).format('YYYY-MM-DD');
  return (
    <span className='tabular-nums text-lg leading-snug text-neutral-600'>
      {formatTime(value)}{isNextDay && <span className='text-xs text-gray-400 ml-1'>({dayjs(value).format('DD/MM')})</span>}
    </span>
  );
};

export const RouteStatusCell = ({ row }: { row: RouteTableRowData }) => {
  const statusLabel = getRouteStatusLabel(row.status) || String(row.status);
  const statusColor =
    STATUS_COLORS[row.status] ?? STATUS_COLORS[RouteStatus.CREATED];
  return <Badge value={statusLabel} {...statusColor} />;
};

export const RouteDriverCell = ({ label }: { label: string }) => (
  <span className={cellTextClass}>{label}</span>
);

export const RouteOperatorCell = ({ label }: { label: string }) => (
  <span className={cellTextClass}>{label || '—'}</span>
);
